import type { DrawObject, DrawProduct } from "@novorender/api";
import type { ReadonlyVec3 } from "gl-matrix";
import { MutableRefObject, useCallback, useEffect, useMemo, useState } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { Canvas2D } from "components";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { type CameraState, drawProduct, getCameraState } from "features/engine2D";
import { selectMainObject } from "features/render";

import { getBrepEdges, getBrepFaces, isFrontFacing, useBrepDisplay } from "./brep";

const SURFACE_FILL = "rgba(255, 0, 255, 0.15)";
const BACK_FACE_FILL = "rgba(0, 200, 255, 0.15)";

export function BrepCanvas({ renderFnRef }: { renderFnRef: MutableRefObject<((moved: boolean) => void) | undefined> }) {
    const {
        state: { view, size },
    } = useExplorerGlobals();
    const { show, angles, surfaces, backFaces } = useBrepDisplay();
    const mainObject = useAppSelector(selectMainObject);
    const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
    const [edges, setEdges] = useState<ReadonlyVec3[][]>();
    const [faces, setFaces] = useState<DrawObject[]>();

    useEffect(() => {
        setEdges(undefined);
        if (!show || mainObject === undefined || !view) {
            return;
        }
        const abort = new AbortController();
        getBrepEdges(view, mainObject, abort.signal).then(
            (edges) => !abort.signal.aborted && setEdges(edges),
            (e: unknown) => !abort.signal.aborted && console.error("Failed to load BREP", e),
        );
        return () => abort.abort();
    }, [show, mainObject, view]);

    useEffect(() => {
        setFaces(undefined);
        if (!show || !surfaces || mainObject === undefined || !view) {
            return;
        }
        const abort = new AbortController();
        getBrepFaces(view, mainObject, abort.signal).then(
            (faces) => !abort.signal.aborted && setFaces(faces),
            (e: unknown) => !abort.signal.aborted && console.error("Failed to load BREP faces", e),
        );
        return () => abort.abort();
    }, [show, surfaces, mainObject, view]);

    const product = useMemo(
        () =>
            edges &&
            mainObject !== undefined &&
            view?.measure?.draw.getDrawObjectFromLineSegments(edges, mainObject, { closed: false, angles }),
        [edges, mainObject, view, angles],
    );

    const facesProduct = useMemo<DrawProduct | undefined>(
        () => faces && { kind: "basic", objects: faces, ObjectId: mainObject },
        [faces, mainObject],
    );

    const draw = useCallback(() => {
        const ctx = canvas?.getContext("2d");
        if (!ctx || !view?.measure) {
            return;
        }
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        const camera = getCameraState(view.renderState.camera);
        if (facesProduct) {
            view.measure.draw.updateProduct(facesProduct);
            const front = facesProduct.objects.filter((face) => isFrontFacing(face, camera));
            if (backFaces) {
                const back = facesProduct.objects.filter((face) => !isFrontFacing(face, camera));
                drawFaces(ctx, camera, facesProduct, back, BACK_FACE_FILL);
            }
            drawFaces(ctx, camera, facesProduct, front, SURFACE_FILL);
        }
        if (product) {
            view.measure.draw.updateProduct(product);
            drawProduct(ctx, camera, product, { lineColor: "magenta" }, 2);
        }
    }, [canvas, view, product, facesProduct, backFaces]);

    useEffect(() => {
        draw();
    }, [draw, size]);

    useEffect(() => {
        renderFnRef.current = (moved) => moved && draw();
        return () => (renderFnRef.current = undefined);
    }, [renderFnRef, draw]);

    return (
        (product || facesProduct) && (
            <Canvas2D id="brep-canvas" ref={setCanvas} width={size.width} height={size.height} />
        )
    );
}

function drawFaces(
    ctx: CanvasRenderingContext2D,
    camera: CameraState,
    product: DrawProduct,
    objects: DrawObject[],
    fillColor: string,
) {
    drawProduct(ctx, camera, { ...product, objects }, { fillColor, lineColor: "transparent" }, 1);
}
